import keys from 'lodash/keys';
import values from 'lodash/values';
import map from 'lodash/map';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import each from 'lodash/each';
import tail from 'lodash/tail';
import split from 'lodash/split';
import chunk from 'lodash/chunk';
import startsWith from 'lodash/startsWith';
import filter from 'lodash/filter';

import {
  LINK_TYPE_NONE,
  LINK_TYPE_OVERLAY,
  LINK_TYPE_DOWNLOADABLE,
  LINK_TYPE_INTERNAL,
  LINK_TYPE_EXTERNAL
} from 'zoomforth_codesbane_viewer/const/link_types.js';

import { getIdFrom } from 'zoomforth_codesbane_viewer/lib/id_utils.js';
import { combineReducers } from 'zoomforth_app_basics/redux_tools.js';

//// Codesbane Reducers

// Viewer
import viewerUI from 'zoomforth_codesbane_viewer/reducers/viewer_ui.js';
import translations from 'zoomforth_codesbane_viewer/reducers/translations.js';

// Editor
import featureFlags, * as fromFeatureFlags from './feature_flags.js';
import lock, * as fromLock from 'zoomforth_tools/concurrent_editing_lock/reducer.js';
import { LOCK_REDUCER_SLICE_NAME } from 'zoomforth_tools/concurrent_editing_lock/const.js';
import editorUI, * as fromEditorUI from './editor_ui.js';
import pageMeta, * as fromPageMeta from './page_meta.js';
import saving, * as fromSaving from 'zoomforth_shared_management/reducers/saving.js';
import theme, * as fromTheme from './theme.js';
import apiFeeds, * as fromApiFeeds from './api_feeds.js';
import siteViewerVersion, * as fromSiteViewerVersion from './site_viewer_version.js';

import pageEntities from './page_entities.js';
import * as fromPageEntities from 'zoomforth_codesbane_viewer/reducers/page_entities.selectors.js';

//// Rest of app reducers

import sharedManagementModals from 'zoomforth_shared_management/reducers/shared_management_modals.js';
import notificationSettings from 'zoomforth_shared_management/reducers/notification_settings.js';
import miscSettings from 'zoomforth_shared_management/reducers/misc_settings.js';
import accessSettings from 'zoomforth_shared_management/reducers/access_settings.js';
import analyticsForSite from 'zoomforth_shared_management/reducers/analytics_for_site.js';
import analyticsForRoute from 'zoomforth_shared_management/reducers/analytics_for_route.js';
import branding from 'zoomforth_shared_management/reducers/branding.js';

import {
  getCurrentSubpage,
  getSiteViewerVersion
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  LATEST_SITE_VIEWER_VERSION,
  getCompatibleStylesheetVersions
} from 'sites_const_shared/site_viewer_versions.js';

export default combineReducers({
  
  // Reducers from the codesbane viewer
  viewerUI,
  pageEntities,
  translations,

  // Reducers just for the codesbane editor
  apiFeeds,
  editorUI,
  [LOCK_REDUCER_SLICE_NAME]: lock,
  theme,
  pageMeta,
  saving,
  featureFlags,
  siteViewerVersion,

  // Reducers from elsewhere in the dashboard project
  sharedManagementModals,
  notificationSettings,
  miscSettings,
  accessSettings,
  analyticsForSite,
  analyticsForRoute,
  branding

});

export const getIsLoadingApiFeeds = (state)=>
  fromApiFeeds.getIsLoadingApiFeeds(state.apiFeeds);

export const getApiFeedById = (state)=>
  fromApiFeeds.getApiFeedById(state.apiFeeds);

export const getApiFeedForId = (state, id)=>
  fromApiFeeds.getApiFeedForId(state.apiFeeds, id);

export const getPendingSave = (state, id)=>
  fromSaving.getPendingSave(state.saving, id);

export const getSendingOrBlockingSave = (state, id)=>
  fromSaving.getSendingOrBlockingSave(state.saving, id);

export const getLastFailure = (state)=>
  fromSaving.getLastFailure(state.saving);

export const getSubpageById = (state, subpageId) =>
  fromPageEntities.getSubpageById(state.pageEntities, subpageId);

export const getCanHideOrDeleteSubpage = (state)=>
  // Cannot hide subpage when there's only one un-hidden subpage
  fromPageEntities.getVisibleSubpageCount(state.pageEntities) > 1;

export const getBlocksData = (state) =>
  fromPageEntities.getBlocksData(state.pageEntities);

export const getContainerBlocksData = (state) =>
  fromPageEntities.getContainerBlocksData(state.pageEntities);

export const getContentBlocksData = (state) =>
  fromPageEntities.getContentBlocksData(state.pageEntities);

export const getPageName = (state)=>
  fromPageMeta.getPageName(state.pageMeta);

export const getIsDraft = (state)=> 
  fromPageMeta.getIsDraft(state.pageMeta);
  
export const getPageHasApprovalPending = (state)=> 
  fromPageMeta.getPageHasApprovalPending(state.pageMeta);

export const getPageIsTemplate = (state)=>
  fromPageMeta.getPageIsTemplate(state.pageMeta);

export const getPageIsSuper = (state)=>
  fromPageMeta.getPageIsSuper(state.pageMeta);

export const getPageHasEverBeenPublished= (state)=>
  fromPageMeta.getPageHasEverBeenPublished(state.pageMeta);

export const getPageCanonicalUrl = (state)=>
  fromPageMeta.getPageCanonicalUrl(state.pageMeta);

export const getPageAllowsNewLinks = (state)=>
  fromPageMeta.getPageAllowsNewLinks(state.pageMeta);

export const getPageId = (state)=>
  fromPageMeta.getPageId(state.pageMeta);

export const getPageUrlId = (state)=>
  fromPageMeta.getPageUrlId(state.pageMeta);

export const getPageMostRecentPublishedVersion = (state)=>
  fromPageMeta.getPageMostRecentPublishedVersion(state.pageMeta);

export const getPageLastEditedByUserId = (state)=>
  fromPageMeta.getLastEditedByUserId(state.pageMeta);

export const getPageLastEditedAt = (state)=>
  fromPageMeta.getLastEditedAt(state.pageMeta);

export const getCurrentlyEditingSection = (state)=> {
  const sid = fromEditorUI.getCurrentlyEditingSectionId(state.editorUI);
  return fromPageEntities.getSectionById(state.pageEntities, sid);
};

export const getCurrentlyEditingContentItems = (state)=> {
  const contentUuids = fromEditorUI.getCurrentlyEditingContentUuids(state.editorUI);
  return map(contentUuids, contentUuid =>
    fromPageEntities.getContentItemByUuid(state.pageEntities, contentUuid)
  );
};

export const getDebugBar = (state)=>
  fromEditorUI.getDebugBar(state.editorUI);

export const getShouldShowFroalaPasteNotification = (state)=>
  fromEditorUI.getShouldShowFroalaPasteNotification(state.editorUI);

export const getIsThemeSelectorOpen = (state)=>
  fromEditorUI.getIsThemeSelectorOpen(state.editorUI);

export const getIsStyleSelectorOpen = (state)=>
  fromEditorUI.getIsStyleSelectorOpen(state.editorUI);

export const getIsTranslationsEditorOpen = (state)=>
  fromEditorUI.getIsTranslationsEditorOpen(state.editorUI);

export const getIsPageScopedCssEditorOpen = (state)=>
  fromEditorUI.getIsPageScopedCssEditorOpen(state.editorUI);

export const getIsPageScopedCssValid = (state)=>
  fromEditorUI.getIsPageScopedCssValid(state.editorUI);

export const getThemes = (state)=> {
  const themes = fromTheme.getThemes(state.theme);

  const siteViewerVersion = getSiteViewerVersion(state);
  const compatibleStylesheetVersions = getCompatibleStylesheetVersions(siteViewerVersion);
  
  return map(themes, (theme)=> {
    return {
      ...theme,
      isCompatibleWithSiteViewerVersion: includes(compatibleStylesheetVersions, theme.stylesheet_version)
    };
  });
};

export const getFilteredThemes = (state)=> {
  var themes = getThemes(state);

  if (getIsShowingCompatibleThemes(state)) {
    themes = getCompatibleThemes(state);
  }

  return themes;
};

export const getCompatibleThemes = (state)=>
  filter(getThemes(state), 'isCompatibleWithSiteViewerVersion');

export const getCompatibleThemesForLatestUpgrade = (state)=> {
  const themes = fromTheme.getThemes(state.theme);
  const compatibleStylesheetVersions = getCompatibleStylesheetVersions(LATEST_SITE_VIEWER_VERSION);
  
  return filter(themes, (theme)=> {
    return includes(compatibleStylesheetVersions, theme.stylesheet_version);
  });
};

export const getIsShowingCompatibleThemes = (state)=>
  fromTheme.getIsShowingCompatibleThemes(state.theme);

export const getIsLoadingThemes = (state)=>
  fromTheme.getIsLoadingThemes(state.theme);

export const getIsHeaderEditorOpen = (state)=>
  fromEditorUI.getIsHeaderEditorOpen(state.editorUI);

export const getCurrentFroalaLinkEditorInstance = (state)=>
  fromEditorUI.getCurrentFroalaLinkEditorInstance(state.editorUI);

export const getCurrentModalName = (state)=>
  fromEditorUI.getCurrentModalName(state.editorUI);

export const getCurrentModalProps = (state)=>
  fromEditorUI.getCurrentModalProps(state.editorUI);

export const getCurrentModalIdent = (state)=>
  fromEditorUI.getCurrentModalIdent(state.editorUI);

export const getEditorOnlySectionOverrides = (state, sectionOrId)=>
  fromEditorUI.getEditorOnlySectionOverrides(state.editorUI, sectionOrId);

export const getEditorOnlySectionOverridesBySectionId = (state) =>
  fromEditorUI.getEditorOnlySectionOverridesBySectionId(state.editorUI);

export const getSaveStatus = (state)=>
  fromSaving.getSaveStatus(state.saving);

export const getIsChoosingTarget = (state)=>
  fromEditorUI.getIsChoosingTarget(state.editorUI);

export const getIsChoosingSection = (state)=>
  fromEditorUI.getIsChoosingSection(state.editorUI);

export const getIsEditingTileTaxonomy = (state) =>
  fromEditorUI.getIsEditingTileTaxonomy(state.editorUI);

export const getIsChoosingContentItem = (state)=>
  fromEditorUI.getIsChoosingContentItem(state.editorUI);

export const getCurrentlyEditingTileCaptionUuid = (state)=>
  fromEditorUI.getCurrentlyEditingTileCaptionUuid(state.editorUI);

export const getIsSidebarExpanded = (state)=>
  fromEditorUI.getIsSidebarExpanded(state.editorUI);

export const getSidebarExpansionSize = (state) =>
  fromEditorUI.getSidebarExpansionSize(state.editorUI);

export const getSidebarOpenedComponentCache = (state) =>
  fromEditorUI.getSidebarOpenedComponentCache(state.editorUI);

export const getChosenContentItem = (state)=> {
  const contentUuid = fromEditorUI.getChosenContentUuid(state.editorUI);
  return fromPageEntities.getContentItemByUuid(state.pageEntities, contentUuid);
};

export const getCanPasteTileData = (state)=>
  fromEditorUI.getCanPasteTileData(state.editorUI);

export const getCanPasteSectionData = (state)=>
  fromEditorUI.getCanPasteSectionData(state.editorUI);

export const getCanPasteSubpageData = (state)=>
  fromEditorUI.getCanPasteSubpageData(state.editorUI);

export const getClipboardItemsByType = (state, type) =>
  fromEditorUI.getClipboardItemsByType(state.editorUI, type);

export const getSectionCreatorSectionData = (state)=>
  fromEditorUI.getSectionCreatorSectionData(state.editorUI);

export const getChosenSection = (state)=> {
  const sectionId = fromEditorUI.getChosenSectionId(state.editorUI);
  return fromPageEntities.getSectionById(state.pageEntities, sectionId);
};

export const getEditorDisplayablePageTitle = (state)=>
  getIsOpenPageTitleEditor(state) ?
    getTemporaryPageTitle(state)
  :
    fromPageEntities.getPageTitle(state.pageEntities);

export const getCurrentSubpageSections = (state) => {
  const currentSubpage = getCurrentSubpage(state);
  const subpageSections = map(currentSubpage.orderedSectionIds, sectionId => 
    fromPageEntities.getSectionById(state.pageEntities, sectionId)
  );

  const sectionCreatorSectionData = getSectionCreatorSectionData(state);

  const { index, section, subpage, tab } = sectionCreatorSectionData;

  if (isEmpty(sectionCreatorSectionData) || subpage !== currentSubpage) {
    return subpageSections;
  }

  // If the section creator is in a tab context, don't add it to the page's section list
  // It will be rendered inside the tab content area instead
  if (tab) {
    return subpageSections;
  }

  return [
    ...subpageSections.slice(0, index),
    section,
    ...subpageSections.slice(index)
  ];
};

export const getIsCurrentSubpageFullPageSection = (state)=> {
  const sections = getCurrentSubpageSections(state);

  return (
    sections.length === 1 &&
    includes(['slideshow', 'videoplaylist', 'pdf', 'text'], sections[0].type)
  );
};

export const getShouldShowFooter = (state)=> {
  const sections = getCurrentSubpageSections(state);
  const isFullPage = getIsCurrentSubpageFullPageSection(state);

  // multisection subpages and fullpage text sections should always show a footer
  return isFullPage ? head(sections).type === 'text' : true;
};

export const getGridSectionSingleTileEditorCurrentTab = (state)=>
  fromEditorUI.getGridSectionSingleTileEditorCurrentTab(state.editorUI);

export const getEditingTabId = (state) =>
  state.pageEntities?.editingTabId || null;

export const getEditingTabParentSectionId = (state) =>
  state.pageEntities?.editingTabParentSectionId || null;

export const getActiveTabId = (state, sectionId) => {
  const activeTabIdBySectionId = fromEditorUI.getActiveTabIdBySectionId(state.editorUI);
  return activeTabIdBySectionId[sectionId] || null;
};

export const getSectionEditorCurrentTab = (state)=>
  fromEditorUI.getSectionEditorCurrentTab(state.editorUI);

export const getEditingOverlayData = (state)=>
  fromEditorUI.getEditingOverlayData(state.editorUI);

export const getTemporaryFroalaLinkData = (state)=> {
  const froalaLinkData = fromEditorUI.getTemporaryFroalaLinkData(state.editorUI);

  if (!froalaLinkData) {
    return froalaLinkData;
  }

  // Only populate section and contentItem if contentUuid exists
  // This prevents errors when editing links in contexts without content items (e.g., tab sections)
  const result = { ...froalaLinkData };
  
  if (froalaLinkData.contentUuid) {
    result.section = fromPageEntities.getSectionForContent(
      state.pageEntities,
      froalaLinkData.contentUuid
    );
    result.contentItem = fromPageEntities.getContentItemByUuid(
      state.pageEntities,
      froalaLinkData.contentUuid
    );
  }

  return result;
};

export const getTemporaryPageTitle = (state)=>
  fromEditorUI.getTemporaryPageTitle(state.editorUI);

export const getTemporaryMenuData = (state)=>
  fromEditorUI.getTemporaryMenuData(state.editorUI);

export const getTemporaryTileActionData = (state)=>
  fromEditorUI.getTemporaryTileActionData(state.editorUI);

export const getIsOpenPageTitleEditor = (state)=>
  fromEditorUI.getIsOpenPageTitleEditor(state.editorUI);

export const getShouldShowGridLines = (state) =>
  fromEditorUI.getShouldShowGridLines(state.editorUI);

export const getLockCurrentUserData = (state)=>
  fromLock.getLockCurrentUserData(state.lock);

export const getLockData = (state)=>
  fromLock.getLockData(state.lock);

export const getUniquePreviousLocks = (state)=>
  fromLock.getUniquePreviousLocks(state.lock);

export const getAppActionForUrl = (state, url, target)=> {
  // this is a fake appAction
  if (!url) {
    return { type: LINK_TYPE_NONE };
  }

  // sometimes users want to see what a button looks like so they choose
  // "none". in this case we insert a `#` as the URL.
  if (url === '#') {
    return { type: LINK_TYPE_NONE };
  }

  // anything that doesn't start with a `#/` will be treated as an external URL
  if (!startsWith(url, '#/')) {
    return { type: LINK_TYPE_EXTERNAL, url, target };
  }

  var subpageId, sectionId, contentUuid, shouldOpenOverlay;

  // chunk the hash into pairs to classify the parts for the returned
  // app_action object. example would be [['subpage', 'SUBPAGEID'], ['section', 'SECTIONID']]
  const chunkedHash = chunk(tail(split(url, '/')), 2);

  each(chunkedHash, ([type, identifier])=> {
    if (type === 'subpage') {
      subpageId = identifier;

    } else if (type === 'section') {
      sectionId = identifier;

    } else if (type === 'content') {
      contentUuid = identifier;

    } else if (type === 'overlay') {
      shouldOpenOverlay = true;
      contentUuid = identifier;

    } else if (type === 'download') {
      contentUuid = identifier;

    }
  });

  // we want the most information we can about the link so if we didn't pick
  // up the section ID from the URL then we grab that data from state
  if (contentUuid && !sectionId) {
    const section = fromPageEntities.getSectionForContent(state.pageEntities, contentUuid);

    if (section) {
      sectionId = getIdFrom(section);
    }
  }

  // sectionless contentUuid means we're definitely dealing with an overlay or
  // downloadable asset
  if (subpageId && contentUuid && !sectionId) {
    if (shouldOpenOverlay) {
      return {
        type: LINK_TYPE_OVERLAY,
        subpageId,
        contentUuid,
        shouldOpenOverlay
      };
    } else {
      return {
        type: LINK_TYPE_DOWNLOADABLE,
        subpageId,
        contentUuid,
        shouldOpenOverlay: false
      };
    }
  }

  return {
    type: LINK_TYPE_INTERNAL,
    subpageId,
    sectionId,
    contentUuid,
    shouldOpenOverlay
  };
};

export const getContentItemsForStylePresetMapping = (state, stylePresetMapping)=> {
  const contentItems = values(state.pageEntities.pageContentItemsByUuid);
  const stylePresetClassnamesToChange = keys(stylePresetMapping);

  return filter(contentItems, (item)=> {
    return includes(stylePresetClassnamesToChange, item.style_preset_classname);
  });
};

export const getOverlayContentItemsToDelete = (state)=>
  fromEditorUI.getOverlayContentItemsToDelete(state.editorUI);

export const getDownloadableContentItemsToDelete = (state)=>
  fromEditorUI.getDownloadableContentItemsToDelete(state.editorUI);

export const getIsSiteViewerVersionUpgradeAvailable = (state)=>
  fromSiteViewerVersion.getIsSiteViewerVersionUpgradeAvailable(state.siteViewerVersion);

export const getHasUserDismissedSiteViewerVersionUpgrade = (state)=>
  fromSiteViewerVersion.getHasUserDismissedSiteViewerVersionUpgrade(state.siteViewerVersion);
